import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import { useScreenTimeEditGuard } from "./ScreenTimeEditGuard.js";

function NavCard({ icon, iconBg, title, subtitle, onPress, customIcon }) {
  return (
    <Pressable style={styles.navCard} onPress={onPress}>
      {customIcon ? (
        customIcon
      ) : (
        <View style={[styles.navIconCircle, { backgroundColor: iconBg }]}>
          <Text style={styles.navIconText}>{icon}</Text>
        </View>
      )}
      <View style={styles.navBody}>
        <Text style={styles.navTitle}>{title}</Text>
        <Text style={styles.navSub}>{subtitle}</Text>
      </View>
      <Text style={styles.chevron}>›</Text>
    </Pressable>
  );
}

/**
 * @param {{
 *   settingsLoaded: boolean,
 *   needsViewUnlock: boolean,
 *   settingsDraft: object,
 *   setSettingsDraft: (fn: (s: object) => object) => void,
 *   hasPasscode: boolean,
 *   passcodeSalt: string,
 *   passcodeHash: string,
 *   onSave: () => Promise<{ ok: boolean, message?: string }> | { ok: boolean, message?: string },
 *   onToggleDailyCheckIn: (enabled: boolean) => void,
 *   onResetCounter: () => void,
 *   onOpenParental: () => void,
 *   onOpenLockboxLab: () => void,
 *   onOpenAppLibrary: () => void,
 *   onOpenAdvanced: () => void,
 * }} props
 */
export function ScreenTimeTabPanel({
  settingsLoaded,
  needsViewUnlock,
  settingsDraft,
  setSettingsDraft,
  hasPasscode,
  passcodeSalt,
  passcodeHash,
  onSave,
  onToggleDailyCheckIn,
  onResetCounter,
  onOpenParental,
  onOpenLockboxLab,
  onOpenAppLibrary,
  onOpenAdvanced,
}) {
  const { guardEdit, editPasscodeModal } = useScreenTimeEditGuard({
    hasPasscode,
    passcodeSalt,
    passcodeHash,
  });
  const [fieldsUnlocked, setFieldsUnlocked] = useState(!hasPasscode);
  const [saveState, setSaveState] = useState("idle");
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    setFieldsUnlocked(!hasPasscode);
  }, [hasPasscode]);

  useEffect(() => {
    if (saveState !== "saved") return undefined;
    const t = setTimeout(() => {
      setSaveState("idle");
      setSaveMessage("");
    }, 3200);
    return () => clearTimeout(t);
  }, [saveState]);

  const unlockFields = () => guardEdit(() => setFieldsUnlocked(true));

  const guardedChange = (updater) => {
    if (hasPasscode && !fieldsUnlocked) {
      unlockFields();
      return;
    }
    updater();
  };

  const handleSave = async () => {
    setSaveState("saving");
    setSaveMessage("");
    try {
      const result = await onSave();
      if (result?.ok) {
        setSaveState("saved");
        setSaveMessage(result.message || "Saved!");
      } else {
        setSaveState("error");
        setSaveMessage(result?.message || "Could not save. Check your inputs.");
      }
    } catch {
      setSaveState("error");
      setSaveMessage("Something went wrong while saving.");
    }
  };

  if (!settingsLoaded) {
    return (
      <View style={styles.loadingWrap}>
        <Text style={styles.loadingText}>Loading Screen Time…</Text>
      </View>
    );
  }

  if (needsViewUnlock) {
    return (
      <View style={styles.loadingWrap}>
        <Text style={styles.lockedText}>
          Screen Time is passcode-protected. Enter your PIN using the unlock screen to view settings.
        </Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.pageTitle}>Screen Time</Text>
        <Text style={styles.pageSub}>Customize your experience</Text>

        <NavCard
          icon="🛡️"
          iconBg="#E0E7FF"
          title="Parental Filter"
          subtitle="Browse freely; adult sites blocked; YouTube/Google Safe Mode"
          onPress={() => guardEdit(onOpenParental)}
        />
        <NavCard
          icon="🔒"
          iconBg="#EDE9FE"
          title="Lockbox Lab"
          subtitle="App blocking & focus modes"
          onPress={() => guardEdit(onOpenLockboxLab)}
        />
        <NavCard
          icon="📱"
          iconBg="#DBEAFE"
          title="App Library"
          subtitle="Manage blocked apps"
          onPress={() => guardEdit(onOpenAppLibrary)}
        />

        <Text style={styles.sectionLabel}>Habit</Text>
        <View style={styles.card}>
          <Text style={styles.fieldLabel}>HABIT NAME</Text>
          <TextInput
            value={settingsDraft.habitName || ""}
            onFocus={() => {
              if (hasPasscode && !fieldsUnlocked) unlockFields();
            }}
            onChangeText={(t) => guardedChange(() => setSettingsDraft((s) => ({ ...s, habitName: t })))}
            style={styles.input}
            placeholderTextColor="#94A3B8"
            editable={!hasPasscode || fieldsUnlocked}
          />
          <Text style={styles.fieldLabel}>GOAL DAYS</Text>
          <TextInput
            value={String(settingsDraft.goalDays ?? "")}
            onFocus={() => {
              if (hasPasscode && !fieldsUnlocked) unlockFields();
            }}
            onChangeText={(t) =>
              guardedChange(() => setSettingsDraft((s) => ({ ...s, goalDays: t.replace(/[^\d]/g, "") })))
            }
            style={styles.input}
            keyboardType="number-pad"
            placeholderTextColor="#94A3B8"
            editable={!hasPasscode || fieldsUnlocked}
          />
          <Text style={styles.fieldLabel}>DAILY REMINDER (HH:MM)</Text>
          <TextInput
            value={settingsDraft.reminderTime || ""}
            onFocus={() => {
              if (hasPasscode && !fieldsUnlocked) unlockFields();
            }}
            onChangeText={(t) => guardedChange(() => setSettingsDraft((s) => ({ ...s, reminderTime: t })))}
            style={styles.input}
            placeholder="08:00"
            placeholderTextColor="#94A3B8"
            maxLength={5}
            editable={!hasPasscode || fieldsUnlocked}
          />
        </View>

        <Text style={styles.sectionLabelDark}>Notifications</Text>
        <View style={styles.card}>
          <View style={styles.toggleRow}>
            <View style={styles.toggleCopy}>
              <Text style={styles.toggleTitle}>Daily check-in</Text>
              <Text style={styles.toggleSub}>Morning quote & 8:00 / 20:00 reminders</Text>
            </View>
            <Switch
              value={!!settingsDraft.dailyCheckInNotificationsEnabled}
              onValueChange={(v) => guardEdit(() => onToggleDailyCheckIn(v))}
              trackColor={{ false: "#CBD5E1", true: "#5EEAD4" }}
              thumbColor={settingsDraft.dailyCheckInNotificationsEnabled ? "#0D9488" : "#F1F5F9"}
            />
          </View>
        </View>

        <Text style={styles.sectionLabelDark}>Privacy</Text>
        <View style={styles.card}>
          <Text style={styles.privacyText}>
            Privacy-first: no blocked URL/domain history is stored. Only aggregated local counters are kept. No
            per-site logs are retained.
          </Text>
          <Pressable onPress={() => guardEdit(onResetCounter)}>
            <Text style={styles.resetLink}>Reset counter</Text>
          </Pressable>
        </View>

        <Pressable
          style={[styles.saveBtn, saveState === "saved" && styles.saveBtnSuccess, saveState === "error" && styles.saveBtnError]}
          onPress={() => guardEdit(() => void handleSave())}
          disabled={saveState === "saving"}
        >
          {saveState === "saving" ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveBtnText}>
              {saveState === "saved" ? "Saved ✓" : saveState === "error" ? "Try again" : "Save Screen Time"}
            </Text>
          )}
        </Pressable>
        {saveMessage ? (
          <Text style={[styles.saveFeedback, saveState === "error" && styles.saveFeedbackError]}>{saveMessage}</Text>
        ) : null}

        <Pressable style={styles.advancedLink} onPress={() => guardEdit(onOpenAdvanced)}>
          <Text style={styles.advancedLinkText}>Advanced blocking & passcode ›</Text>
        </Pressable>
      </ScrollView>
      {editPasscodeModal}
    </>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: "transparent" },
  content: { padding: 16, paddingBottom: 32 },
  loadingWrap: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24, backgroundColor: "transparent" },
  loadingText: { color: "#0F766E", fontWeight: "700" },
  lockedText: { color: "#475569", textAlign: "center", lineHeight: 22 },
  pageTitle: { fontSize: 26, fontWeight: "800", color: "#0F172A" },
  pageSub: { fontSize: 14, color: "#64748B", marginBottom: 16 },
  navCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    shadowColor: "#0F766E",
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  navIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  navIconText: { fontSize: 20 },
  navBody: { flex: 1 },
  navTitle: { fontSize: 16, fontWeight: "800", color: "#0F172A" },
  navSub: { fontSize: 12, color: "#64748B", marginTop: 2 },
  chevron: { fontSize: 22, color: "#94A3B8", fontWeight: "300" },
  sectionLabel: { fontSize: 15, fontWeight: "800", color: "#0D9488", marginTop: 8, marginBottom: 8 },
  sectionLabelDark: { fontSize: 16, fontWeight: "800", color: "#0F172A", marginTop: 12, marginBottom: 8 },
  card: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 8,
    shadowColor: "#0F766E",
    shadowOpacity: 0.05,
    shadowRadius: 6,
    elevation: 1,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: "800",
    color: "#64748B",
    letterSpacing: 0.8,
    marginBottom: 6,
    marginTop: 8,
  },
  input: {
    backgroundColor: "#CCFBF1",
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: "#0F172A",
    borderWidth: 1,
    borderColor: "#99F6E4",
  },
  toggleRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  toggleCopy: { flex: 1, paddingRight: 12 },
  toggleTitle: { fontSize: 15, fontWeight: "700", color: "#0F172A" },
  toggleSub: { fontSize: 12, color: "#64748B", marginTop: 2 },
  privacyText: { fontSize: 13, color: "#475569", lineHeight: 20 },
  resetLink: { color: "#DC2626", fontWeight: "700", marginTop: 12, fontSize: 14 },
  saveBtn: {
    backgroundColor: "#14B8A6",
    borderRadius: 999,
    paddingVertical: 16,
    alignItems: "center",
    marginTop: 16,
    minHeight: 52,
    justifyContent: "center",
  },
  saveBtnSuccess: { backgroundColor: "#0D9488" },
  saveBtnError: { backgroundColor: "#DC2626" },
  saveBtnText: { color: "#fff", fontWeight: "800", fontSize: 16 },
  saveFeedback: {
    marginTop: 10,
    textAlign: "center",
    fontSize: 13,
    fontWeight: "700",
    color: "#0D9488",
  },
  saveFeedbackError: { color: "#DC2626" },
  advancedLink: { alignItems: "center", marginTop: 14, paddingVertical: 8 },
  advancedLinkText: { color: "#0D9488", fontWeight: "700", fontSize: 14 },
});
